import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialColorOutput, materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {TextureGradientType} from "#material/nodes/texture/tex-gradient/types"
import {textureGradientTypeSchema} from "#material/nodes/texture/tex-gradient/types"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {z} from "zod/v4-mini"
import {color} from "@cm/graph-v3"
import {vec3} from "#material/types/vector"
import {keepLegacyColorMapping, keepLegacyTextureMapping} from "#material/nodes/texture/legacy-texture-settings"

export interface TexGradientSockets extends MaterialNodeSockets {
    inputs: {
        vector: "point" // found in inputs: point; found in settings: vector; inferred from data: float3
    }
    settings: {
        color: "color" // found in inputs: -; found in settings: color; inferred from data: float3
        fac: "float" // found in inputs: -; found in settings: float; inferred from data: float
        vector: "point" // found in inputs: point; found in settings: vector; inferred from data: float3
    }
    settingsExtensions: {
        gradientType?: TextureGradientType // found in inputs: -; found in settings: string; inferred from data: string
    }
    outputs: {
        color: "color"
        fac: "float"
    }
}

export const texGradientDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeTexGradient,
    },
    nodeClass: "TexGradient",
    ui: {
        label: "Gradient Texture",
        color: "#4987af",
        items: [
            {name: "color", valueType: "color", label: "Value", output: true},
            {name: "fac", valueType: "float", label: "Fac", output: true},
            {name: "vector", valueType: "point", label: "Vector", input: true, defaultValue: {x: 0, y: 0, z: 0}},
            {
                name: "gradientType",
                defaultValue: "LINEAR",
                label: "Type",
                valueType: "string",
                options: [
                    {label: "Linear", value: "LINEAR"},
                    {label: "Quadratic", value: "QUADRATIC"},
                    {label: "Easing", value: "EASING"},
                    {label: "Diagonal", value: "DIAGONAL"},
                    {label: "Spherical", value: "SPHERICAL"},
                    {label: "Quadratic Sphere", value: "QUADRATIC_SPHERE"},
                    {label: "Radial", value: "RADIAL"},
                ],
                input: false,
                setting: true,
            },
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({color: materialColorOutput, fac: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({vector: optionalMaterialInput}),
            settings: z.strictObject({
                color: z.optional(color),
                ...keepLegacyColorMapping,
                fac: z.optional(z.number()),
                gradientType: z.optional(textureGradientTypeSchema),
                ...keepLegacyTextureMapping,
                vector: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<TexGradientSockets>
